'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Autoplay,
  Scrollbar,
  Mousewheel,
  Keyboard,
  Navigation,
} from 'swiper/modules';
import { Swiper } from 'swiper/react';
import { cn } from '@/utils/cn';
import {
  screenTypes,
  sliderMainClassTypes,
  sliderNavigationTypes,
} from '@/constants/types';
import useWindowDimensions from '@/app/hooks/useWindowSize';

import 'swiper/css';
import 'swiper/css/scrollbar';
import './custom_scrollbar.css';
import 'swiper/css/mousewheel';

function SliderWrapper({
  children,
  data,
  outerDivClass = 'h-auto relative',
  containerRefClass = 'container mx-auto',
  sliderMainClassType = sliderMainClassTypes.default,
  sliderNavigationType = sliderNavigationTypes.withoutNavigation,
  prevButtonRef,
  nextButtonRef,
  isGlobalSearchPopup = false,
  onlyForThreeProductSlider = false,
}) {
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const windowDimensions = useWindowDimensions();
  const swiperRef = useRef(null);
  const hiddenDivRef = useRef(null);
  const prevButton = prevButtonRef || useRef(null);
  const nextButton = nextButtonRef || useRef(null);
  const [isScreenSmaller, setIsScreenSmaller] = useState(false);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiperInstance = swiperRef.current.swiper;

      // Set navigation elements if slider has navigation enabled
      if (sliderNavigationType === sliderNavigationTypes.withNavigation) {
        swiperInstance.params.navigation.prevEl = prevButton.current;
        swiperInstance.params.navigation.nextEl = nextButton.current;
        swiperInstance.navigation.init();
        swiperInstance.navigation.update();
      }

      const checkScrollbar = () => {
        setHasScrollbar(!swiperInstance.isLocked);
      };

      swiperInstance.on('slideChange', checkScrollbar);
      swiperInstance.on('resize', checkScrollbar);
      checkScrollbar();

      return () => {
        swiperInstance.off('slideChange', checkScrollbar);
        swiperInstance.off('resize', checkScrollbar);
      };
    }
  }, [data, prevButton?.current, nextButton?.current]);

  const [startPosition, setStartPosition] = useState(0);

  useEffect(() => {
    const updateStartPosition = () => {
      if (hiddenDivRef.current) {
        const rect = hiddenDivRef.current.getBoundingClientRect();

        if (
          sliderMainClassType === sliderMainClassTypes.justForYou &&
          isGlobalSearchPopup
        ) {
          if (windowDimensions.width <= screenTypes.large) {
            // mobile
            setStartPosition(16);
          } else {
            // extra large
            setStartPosition(32);
          }
        } else if (windowDimensions.width <= screenTypes.medium) {
          // mobile
          setStartPosition(rect.left + 16);
        } else if (
          windowDimensions.width > screenTypes.medium &&
          windowDimensions.width < screenTypes.extraLarge
        ) {
          // tablet - large
          setStartPosition(rect.left + 40);
        } else if (windowDimensions.width > screenTypes.extraLarge) {
          // extra large
          setStartPosition(rect.left);
        }
      }
    };

    updateStartPosition();
    window.addEventListener('resize', updateStartPosition);
    return () => window.removeEventListener('resize', updateStartPosition);
  }, [windowDimensions]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        setIsScreenSmaller(window.innerWidth > 678 && window.innerWidth < 1023);
      };
      handleResize();

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const getMainClass = (type = sliderMainClassTypes.default) => {
    if (type === sliderMainClassTypes.justForYou) {
      return `product-details frequently-bought recently-viewed-slider !pb-[20px] lg:!pb-[24px] !pt-[16px] lg:!pt-[20px] just-for-you`;
    }

    if (type === sliderMainClassTypes.threeProduct) {
      return cn(
        'product-details frequently-bought recently-viewed-slider !border-none !background-transparent 2xl:!px-16 !pb-[28px] lg:!pb-[24px] !pt-6 lg:!pt-12 transition duration-75 ease-in-linear',
        isScreenSmaller && 'three-products-slider',
      );
    }

    return cn(
      '!border-none !background-transparent !pb-[22px] lg:!pb-[24px] !pt-[12px] lg:!pt-[20px] transition duration-75 ease-in-linear default',
    );
  };

  const getSpaceBasedOnBreakpoint = (
    type = sliderNavigationTypes.withoutNavigation,
  ) => {
    if (type === sliderNavigationTypes.withNavigation) {
      if (onlyForThreeProductSlider) {
        return {
          250: {
            spaceBetween: 10,
          },
          1024: {
            spaceBetween: 18,
          },
        };
      }

      return {
        250: {
          spaceBetween: 8,
        },
        1024: {
          spaceBetween: 24,
        },
      };
    }

    if (type === sliderNavigationTypes.withNavigationCampaign) {
      return {
        250: {
          spaceBetween: 6,
        },
        1024: {
          spaceBetween: 16,
        },
      };
    }

    return {
      0: { spaceBetween: 10 },
      1024: { spaceBetween: 20 },
    };
  };

  const getSwiperModule = (type = sliderNavigationTypes.withoutNavigation) => {
    if (type === sliderNavigationTypes.withoutNavigation) {
      return [Autoplay, Scrollbar, Keyboard, Mousewheel];
    }

    return [Autoplay, Scrollbar, Keyboard, Mousewheel, Navigation];
  };

  return (
    <div className={cn('slider-wrapper', outerDivClass)}>
      {/* TODO: will remove later */}
      <div className={containerRefClass} ref={hiddenDivRef}>
        {/* {startPosition} */}
      </div>
      <Swiper
        ref={swiperRef}
        modules={getSwiperModule(sliderNavigationType)}
        slidesPerView="auto"
        breakpoints={getSpaceBasedOnBreakpoint(sliderNavigationType)}
        scrollbar={{ hide: false, draggable: true }}
        keyboard={{ enabled: true }}
        mousewheel={{ forceToAxis: true }}
        className={cn(getMainClass(sliderMainClassType))}
        style={{
          paddingLeft: `${startPosition}px`,
          paddingRight: `${startPosition}px`,
        }}
        navigation={{
          prevEl: prevButton.current,
          nextEl: nextButton.current,
        }}
      >
        {children}
      </Swiper>
    </div>
  );
}

export default SliderWrapper;
